import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send, ImagePlus, X, ShieldCheck, Loader2, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { apiError, getUser, mediaUrl } from "../lib/api";

export const ChatThread = ({ conversation, ctx, allowCopy = false, showUserInfo = false, extraActions = null, onUpdated }) => {
  const viewer = getUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const lastTsRef = useRef(null);
  const convId = conversation?.id;
  const closed = conversation?.status === "closed";

  const scrollToBottom = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  };

  const poll = useCallback(async () => {
    if (!convId) return;
    try {
      const fresh = await ctx.messages(convId, lastTsRef.current);
      if (fresh.length) {
        lastTsRef.current = fresh[fresh.length - 1].created_at;
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => m.id));
          const merged = [...prev, ...fresh.filter((m) => !ids.has(m.id))];
          return merged;
        });
        setTimeout(scrollToBottom, 50);
      }
    } catch (e) { /* silent poll error */ }
  }, [convId, ctx]);

  useEffect(() => {
    setMessages([]);
    lastTsRef.current = null;
    if (!convId) return;
    poll();
    const iv = setInterval(poll, 2000);
    return () => clearInterval(iv);
  }, [convId, poll]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending || closed) return;
    setInput("");
    setSending(true);
    try {
      await ctx.sendMessage(convId, { text });
      await poll();
    } catch (err) { toast.error(apiError(err)); setInput(text); }
    finally { setSending(false); }
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { image_url } = await ctx.uploadImage(file);
      await ctx.sendMessage(convId, { image_url });
      await poll();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doClose = async () => {
    try {
      const c = await ctx.close(convId);
      toast.success("Conversación finalizada.");
      onUpdated?.(c);
      await poll();
    } catch (err) { toast.error(apiError(err)); }
  };

  const guard = allowCopy ? {} : {
    onCopy: (e) => { e.preventDefault(); toast.info("La conversación está protegida."); },
    onContextMenu: (e) => e.preventDefault(),
    style: { userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" },
  };

  const staff = conversation?.staff;
  const isGroup = conversation?.kind === "family";

  return (
    <div className="flex flex-col h-full bg-[#FAF8F1]" data-testid="chat-thread">
      {/* Header */}
      <div className="bg-navy px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="min-w-0">
          {showUserInfo ? (
            <>
              <p className="font-extrabold text-white leading-tight truncate">{conversation?.owner_name || "Usuario"}</p>
              <p className="text-xs text-white/60 truncate">
                {[conversation?.owner_programa, conversation?.owner_pais].filter(Boolean).join(" · ")}
                {conversation?.owner_referencia ? ` · Ref: ${conversation.owner_referencia}` : ""}
                {isGroup ? " · Grupo familiar" : ""}
              </p>
            </>
          ) : (
            <>
              <p className="font-extrabold text-white leading-tight truncate flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-gold" />
                {staff ? staff.nombre : "Staff Supera"}
              </p>
              <p className="text-xs text-white/60">
                {conversation?.status === "waiting" ? "Esperando a un asesor..." : staff ? "Asesor Supera en línea" : "Chat Supera"}
                {isGroup ? " · Grupo familiar" : ""}
              </p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {extraActions}
          {!closed && (
            <button data-testid="chat-close-btn" onClick={doClose} className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 transition-colors">
              <X className="w-3.5 h-3.5" /> Finalizar
            </button>
          )}
        </div>
      </div>

      {/* Copy-protection banner for users */}
      {!allowCopy && (
        <div className="bg-[#0A1C30]/5 text-[#6B7688] text-[11px] px-4 py-1.5 flex items-center gap-1.5 flex-shrink-0">
          <Lock className="w-3 h-3" /> Conversación protegida y confidencial.
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto supera-scroll px-4 py-4 space-y-2.5" {...guard} data-testid="chat-messages">
        {messages.map((m) => {
          if (m.sender_role === "system") {
            return (
              <div key={m.id} className="flex justify-center">
                <span className="bg-[#0A1C30]/8 text-[#4A5568] text-xs font-semibold px-3 py-1.5 rounded-full text-center max-w-[85%]">{m.text}</span>
              </div>
            );
          }
          const mine = m.mine;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${mine ? "bg-[#0A1C30] text-white rounded-br-md" : "bg-white text-[#2A3446] ring-1 ring-black/5 rounded-bl-md"}`}>
                {!mine && (isGroup || m.sender_role === "staff" || m.sender_role === "admin") && (
                  <p className={`text-[11px] font-extrabold mb-0.5 ${m.sender_role === "staff" || m.sender_role === "admin" ? "text-[#B9860B]" : "text-[#3B7DF6]"}`}>{m.sender_name}</p>
                )}
                {m.image_url && (
                  <img src={mediaUrl(m.image_url)} alt="adjunto" className="rounded-xl mb-1 max-h-64 w-auto object-contain bg-black/5" draggable={false} />
                )}
                {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-[#9AA3B2]"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      {closed ? (
        <div className="p-4 border-t border-black/5 bg-white text-center text-sm font-semibold text-[#6B7688] flex items-center justify-center gap-2 flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-[#22A860]" /> Esta conversación está finalizada.
        </div>
      ) : (
        <form onSubmit={send} className="p-3 border-t border-black/5 bg-white flex items-center gap-2 flex-shrink-0">
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickImage} data-testid="chat-image-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-11 h-11 flex-shrink-0 rounded-full bg-[#F4F6F9] hover:bg-[#E9ECF1] flex items-center justify-center transition-colors disabled:opacity-50" data-testid="chat-attach-btn">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#6B7688]" /> : <ImagePlus className="w-5 h-5 text-[#6B7688]" />}
          </button>
          <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribe tu mensaje..." className="flex-1 h-11 px-4 rounded-full bg-[#F4F6F9] text-sm text-[#0A1C30] outline-none focus:ring-2 focus:ring-[var(--gold)] transition-shadow" />
          <button type="submit" data-testid="chat-send-btn" disabled={sending || !input.trim()} className="w-11 h-11 flex-shrink-0 rounded-full bg-[var(--gold)] hover:bg-[var(--gold-soft)] disabled:opacity-50 flex items-center justify-center transition-colors">
            <Send className="w-5 h-5 text-[#0A1C30]" />
          </button>
        </form>
      )}
    </div>
  );
};
